import { ReactNode } from "react";
import { Link } from "react-router-dom";

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  step?: number;
  totalSteps?: number;
  subStep?: number;
  totalSubSteps?: number;
}

const steps = ["Account", "Contract", "Payment", "Dashboard"];

export default function AuthLayout({
  children,
  title,
  subtitle,
  step,
  totalSteps,
  subStep,
  totalSubSteps,
}: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#16a34a] rounded-lg flex items-center justify-center">
              <i className="ri-sun-line text-white text-sm"></i>
            </div>
            <span className="font-bold text-gray-900 text-lg tracking-tight">
              SolarLead<span className="text-[#16a34a]">Source</span>
            </span>
          </Link>
          <div className="text-sm text-gray-500">
            Need help?{" "}
            <a href="mailto:support@solarleadsource.com" className="text-[#16a34a] hover:underline">
              support@solarleadsource.com
            </a>
          </div>
        </div>
      </header>

      {/* Progress steps */}
      {step !== undefined && totalSteps !== undefined && (
        <div className="bg-white border-b border-gray-100 px-6 py-5">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between">
              {steps.map((s, i) => {
                const stepNum = i + 1;
                const isCompleted = stepNum < step;
                const isActive = stepNum === step;
                return (
                  <div key={s} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                          isCompleted
                            ? "bg-[#16a34a] text-white"
                            : isActive
                            ? "bg-[#16a34a] text-white ring-4 ring-green-100"
                            : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {isCompleted ? (
                          <i className="ri-check-line text-sm"></i>
                        ) : (
                          stepNum
                        )}
                      </div>
                      <span
                        className={`text-xs mt-1.5 font-medium whitespace-nowrap ${
                          isActive
                            ? "text-[#16a34a]"
                            : isCompleted
                            ? "text-gray-600"
                            : "text-gray-400"
                        }`}
                      >
                        {s}
                      </span>
                    </div>
                    {i < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 mb-5 transition-all ${
                          isCompleted ? "bg-[#16a34a]" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Sub-step indicator (e.g. "Step 1 of 2" within Account) */}
            {subStep !== undefined && totalSubSteps !== undefined && totalSubSteps > 1 && (
              <div className="mt-4 flex items-center gap-3">
                <div className="flex gap-1.5">
                  {Array.from({ length: totalSubSteps }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 rounded-full transition-all ${
                        i < subStep
                          ? "bg-[#16a34a] w-8"
                          : "bg-gray-200 w-4"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs text-gray-400 font-medium">
                  {subStep === 1 ? "Account info" : "Service area"} &mdash; {subStep} of {totalSubSteps}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-2xl">
          <div className="mb-8 text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{title}</h1>
            {subtitle && <p className="text-gray-500 text-base">{subtitle}</p>}
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}